import * as React from 'react';
import { cn } from '@/util/cn';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { ItemsContainer } from '../ItemsContainer';
import { NearbyResortCard, type NearbyResort } from '../NearbyResortCard';

export type { NearbyResort } from '../NearbyResortCard';

// Nearby Resorts list used on resort / region surfaces. Built on top of
// ItemsContainer as a single-row carousel: mobile swipes,
// desktop gets chevron arrows. Cards never wrap onto a second row.
//
// Each card: 16:10 image (subtle scale on hover) + bold name +
// region subtitle (optional · distance). The card itself is the
// click target.
//
// `isSummerMode` tints the heading, the "view all" link and the card
// scrim in the summer green so the section reads as the off-season
// variant on the Resort Summer Overview surface. The default winter
// palette uses secondary/primary.

export type NearbyResortsProps = {
  resorts: NearbyResort[];
  /** Section heading. Defaults to "Nearby Resorts". */
  heading?: string;
  /** Optional right-aligned link in the heading row. */
  link?: { href: string; label: string };
  /** Visible cards in the rail at desktop. Defaults to 3. */
  desktopColumns?: 2 | 3 | 4;
  /** Tints the heading, the "view all" link and the card scrim in the
   *  summer green for the summer overview surface. */
  isSummerMode?: boolean;
  className?: string;
};

export const NearbyResorts = ({
  resorts,
  heading,
  link,
  desktopColumns = 3,
  isSummerMode,
  className,
}: NearbyResortsProps) => {
  const { t } = useOTSTranslation('common');
  const title = heading ?? t('homePage.nearbyResorts');
  return (
    <ItemsContainer
      title={title}
      viewAllHref={link?.href}
      viewAllLabel={link?.label}
      desktopLayout="carousel"
      desktopColumns={desktopColumns}
      ariaLabel={title}
      isSummerMode={isSummerMode}
      className={cn('mb-14', className)}
    >
      {resorts.map((r) => (
        <ItemsContainer.Item key={r.name}>
          <NearbyResortCard resort={r} isSummerMode={isSummerMode} />
        </ItemsContainer.Item>
      ))}
    </ItemsContainer>
  );
};
